import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFocused } from '@react-navigation/native';
import { DrawerMenuIcon } from '../icons/DrawerMenuIcon';
import { useWebDrawer } from '../layout/WebDrawerContext';

/** Diameter of every circular control sitting on a page-header row. */
export const WEB_HEADER_CONTROL_SIZE = 40;

export interface WebDrawerMenuButtonProps {
  /** Override the 40px default when a header runs a tighter control row. */
  size?: number;
  style?: CSSProperties;
}

/**
 * The drawer hamburger as an *in-flow* header control.
 *
 * Renders nothing on desktop (the sidebar is always on screen) or outside the
 * protected shell. While one is mounted the shell drops its floating fallback
 * button — see the `:has(.tabbio-inline-menu-btn)` rule in global.css — so the
 * two never double up.
 *
 * WebPageHeader places one automatically. Screens whose header is not a title
 * row (a search pill, a conversations panel) mount it directly.
 */
export function WebDrawerMenuButton({
  size = WEB_HEADER_CONTROL_SIZE,
  style,
}: WebDrawerMenuButtonProps) {
  const drawer = useWebDrawer();
  const { t } = useTranslation();
  // expo-router keeps navigated-away screens mounted, collapsed to 0x0 inside
  // an `aria-hidden` container. `:has()` is structural — it matches those
  // leftovers too — so an unfocused screen's button would keep suppressing the
  // fallback on a route that paints no button of its own, leaving no way to
  // open the drawer at all. Unmount on blur so `:has()` only ever sees the
  // live one. (Same trap `app/index.tsx` guards with useIsFocused.)
  const isFocused = useIsFocused();
  const [hover, setHover] = useState(false);

  if (!drawer?.isMobile || !isFocused) return null;

  return (
    <button
      type="button"
      className="tabbio-inline-menu-btn"
      onClick={drawer.toggleDrawer}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={t('openSidebar')}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backgroundColor: hover ? 'var(--hover-strong)' : 'transparent',
        color: 'var(--text-secondary)',
        flexShrink: 0,
        transition: 'background-color 150ms ease',
        fontFamily: 'inherit',
        ...style,
      }}
    >
      <DrawerMenuIcon size={22} />
    </button>
  );
}
